import React, { useEffect, useState } from "react";
import { getPageBySlug } from "../lib/cms";
import { 
  Phone, 
  Mail, 
  MapPin, 
  Calendar, 
  HeartHandshake, 
  Clock, 
  ShieldCheck, 
  CheckCircle2, 
  ChevronDown, 
  ChevronUp, 
  User, 
  ArrowRight, 
  ExternalLink, 
  AlertCircle
} from "lucide-react";

export default function Forstehjaelp() {
  const [pageData, setPageData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("all");
  const [showCmsContent, setShowCmsContent] = useState(false);

  useEffect(() => {
    async function loadData() {
      try {
        const page = await getPageBySlug("forstehjaelp");
        setPageData(page);
      } catch (err) {
        console.error("Error loading page data:", err);
      } finally {
        setLoading(false);
      }
    }
    loadData();
  }, []);

  useEffect(() => {
    if (pageData) {
      document.title = pageData.title;
      const metaDesc = document.querySelector('meta[name="description"]');
      if (metaDesc) {
        metaDesc.setAttribute("content", pageData.meta_description || "");
      }
    }
  }, [pageData]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-brand-light">
        <div className="animate-pulse text-brand-primary font-bold text-lg flex items-center gap-2">
          <HeartHandshake className="animate-bounce h-6 w-6 text-brand-primary" />
          Henter indhold...
        </div>
      </div>
    );
  }

  // Course dates parsed from CMS content
  const courseDates = [
    { id: 13, month: "Juni", day: "27", year: "2026", weekday: "Lørdag", status: "Fuld booket" },
    { id: 14, month: "Juli", day: "11", year: "2026", weekday: "Lørdag", status: "Ledige pladser" },
    { id: 15, month: "Juli", day: "25", year: "2026", weekday: "Lørdag", status: "Ledige pladser" },
    { id: 16, month: "August", day: "08", year: "2026", weekday: "Lørdag", status: "Ledige pladser" },
    { id: 17, month: "August", day: "22", year: "2026", weekday: "Lørdag", status: "Ledige pladser" },
    { id: 18, month: "September", day: "12", year: "2026", weekday: "Lørdag", status: "Ledige pladser" },
    { id: 19, month: "September", day: "26", year: "2026", weekday: "Lørdag", status: "Ledige pladser" }
  ];

  const months = ["all", "Juni", "Juli", "August", "September"];

  const filteredDates = activeTab === "all" 
    ? courseDates 
    : courseDates.filter(d => d.month === activeTab);

  return (
    <div className="bg-brand-light min-h-screen">
      {/* Hero Section */}
      <section className="relative py-16 lg:py-24 overflow-hidden bg-gradient-to-b from-stone-100 to-brand-light">
        <div className="absolute inset-0 opacity-40 bg-[radial-gradient(#e5e7eb_1px,transparent_1px)] [background-size:16px_16px]"></div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="max-w-3xl mx-auto text-center">
            
            {/* Centered Content: Text */}
            <div className="space-y-6 flex flex-col items-center">
              <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-brand-primary/10 text-brand-primary text-xs font-bold uppercase tracking-wider">
                <ShieldCheck className="h-4 w-4" /> Lovkrav til kørekort
              </span>
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black text-brand-dark tracking-tight leading-none">
                Førstehjælp til <br />
                <span className="text-brand-primary">Kørekortet</span>
              </h1>
              <p className="text-stone-600 text-lg sm:text-xl leading-relaxed max-w-2xl mx-auto">
                Inden du kan gå op til teoriprøven, skal du have gennemført et godkendt færdselsrelateret førstehjælpskursus. 
                Hos Carinas Køreskole samarbejder vi med dygtige og professionelle instruktører, så du får et lærerigt, 
                trygt og engagerende kursus.
              </p>
              
              <div className="flex flex-wrap justify-center gap-4 pt-2">
                <a 
                  href="#datoer" 
                  className="px-6 py-3.5 bg-brand-primary text-white font-bold rounded-xl hover:bg-brand-primary-hover shadow-md hover:shadow-lg transition-all duration-200 text-sm flex items-center gap-2"
                >
                  Se kursusdatoer <ArrowRight className="h-4 w-4" />
                </a>
                <a 
                  href="#instruktoer" 
                  className="px-6 py-3.5 bg-white text-brand-dark border border-stone-200 font-bold rounded-xl hover:bg-stone-50 transition-all duration-200 text-sm flex items-center gap-2"
                >
                  Kontakt instruktør
                </a>
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* Quick Facts Grid */}
      <section className="py-12 bg-white border-y border-stone-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-6 sm:gap-8">
            
            <div className="flex items-start gap-4 p-4 rounded-2xl hover:bg-stone-50 transition-colors">
              <div className="p-3 bg-brand-primary/10 rounded-xl text-brand-primary shrink-0">
                <HeartHandshake className="h-6 w-6" />
              </div>
              <div className="text-left">
                <p className="text-xs font-semibold text-stone-400 uppercase tracking-wider">Pris</p>
                <p className="text-lg font-black text-brand-dark">700 kr.</p>
                <p className="text-xs text-stone-500">Betales til instruktør</p>
              </div>
            </div>

            <div className="flex items-start gap-4 p-4 rounded-2xl hover:bg-stone-50 transition-colors">
              <div className="p-3 bg-brand-primary/10 rounded-xl text-brand-primary shrink-0">
                <Clock className="h-6 w-6" />
              </div>
              <div className="text-left">
                <p className="text-xs font-semibold text-stone-400 uppercase tracking-wider">Varighed</p>
                <p className="text-lg font-black text-brand-dark">8 timer</p>
                <p className="text-xs text-stone-500">Færdselsrelateret</p>
              </div>
            </div>

            <div className="flex items-start gap-4 p-4 rounded-2xl hover:bg-stone-50 transition-colors">
              <div className="p-3 bg-brand-primary/10 rounded-xl text-brand-primary shrink-0">
                <Calendar className="h-6 w-6" />
              </div>
              <div className="text-left">
                <p className="text-xs font-semibold text-stone-400 uppercase tracking-wider">Gyldighed</p>
                <p className="text-lg font-black text-brand-dark">1 år</p>
                <p className="text-xs text-stone-500">Fra udstedelsesdato</p>
              </div>
            </div>

            <div className="flex items-start gap-4 p-4 rounded-2xl hover:bg-stone-50 transition-colors">
              <div className="p-3 bg-brand-primary/10 rounded-xl text-brand-primary shrink-0">
                <MapPin className="h-6 w-6" />
              </div>
              <div className="text-left">
                <p className="text-xs font-semibold text-stone-400 uppercase tracking-wider">Lokation</p>
                <p className="text-lg font-black text-brand-dark">Aalborg C</p>
                <p className="text-xs text-stone-500">Hasserisgade 5</p>
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* Main Content: Dates & Instructor */}
      <section id="datoer" className="py-16 lg:py-24 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
          
          {/* Left Side: Interactive Calendar */}
          <div className="lg:col-span-8 space-y-8">
            <div className="text-left">
              <h2 className="text-3xl font-black text-brand-dark tracking-tight">
                Kursusdatoer i 2026
              </h2>
              <p className="text-stone-600 mt-2">
                Find et hold, der passer dig. Du tilmelder dig direkte hos instruktøren Michael via SMS eller e-mail.
              </p>
            </div>

            {/* Month Tabs */}
            <div className="flex flex-wrap gap-2 pb-2 border-b border-stone-200">
              {months.map((month) => (
                <button
                  key={month}
                  onClick={() => setActiveTab(month)}
                  className={`px-4 py-2 rounded-xl text-xs sm:text-sm font-bold transition-all duration-200 ${
                    activeTab === month
                      ? "bg-brand-primary text-white shadow-md"
                      : "bg-white text-stone-600 border border-stone-200 hover:bg-stone-50"
                  }`}
                >
                  {month === "all" ? "Alle måneder" : month}
                </button>
              ))}
            </div>

            {/* Dates Grid */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {filteredDates.map((date) => (
                <div 
                  key={date.id}
                  className={`bg-white rounded-2xl border p-5 flex items-center justify-between transition-all duration-200 hover:shadow-md ${
                    date.status === "Fuld booket" 
                      ? "border-stone-200 opacity-75" 
                      : "border-stone-200 hover:border-brand-primary/30"
                  }`}
                >
                  <div className="flex items-center gap-4 text-left">
                    {/* Date Badge */}
                    <div className="h-14 w-14 rounded-xl bg-brand-primary/5 border border-brand-primary/10 flex flex-col items-center justify-center shrink-0">
                      <span className="text-xs font-bold text-brand-primary uppercase leading-none">{date.month.substring(0, 3)}</span>
                      <span className="text-xl font-black text-brand-dark leading-none mt-1">{date.day}</span>
                    </div>
                    
                    <div>
                      <p className="font-bold text-brand-dark text-sm sm:text-base">{date.weekday}</p>
                      <p className="text-xs text-stone-500">{date.month} {date.year}</p>
                      
                      {/* Status Badge */}
                      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold mt-1.5 ${
                        date.status === "Ledige pladser"
                          ? "bg-emerald-50 text-emerald-600"
                          : date.status === "Få pladser"
                          ? "bg-amber-50 text-amber-600"
                          : "bg-rose-50 text-rose-600"
                      }`}>
                        <span className={`h-1.5 w-1.5 rounded-full ${
                          date.status === "Ledige pladser"
                            ? "bg-emerald-500"
                            : date.status === "Få pladser"
                            ? "bg-amber-500"
                            : "bg-rose-500"
                        }`}></span>
                        {date.status}
                      </span>
                    </div>
                  </div>

                  {date.status !== "Fuld booket" && (
                    <a 
                      href={`sms:61661084?body=Hej Michael, jeg vil gerne tilmelde mig førstehjælp d. ${date.day}. ${date.month} ${date.year}`}
                      className="p-2.5 bg-stone-50 hover:bg-brand-primary hover:text-white rounded-xl text-stone-600 transition-all duration-200"
                      title="Tilmeld via SMS"
                    >
                      <ArrowRight className="h-4 w-4" />
                    </a>
                  )}
                </div>
              ))}
            </div>

            {filteredDates.length === 0 && (
              <div className="bg-white rounded-2xl border border-stone-200 p-8 text-center">
                <p className="text-stone-500 font-medium">Ingen planlagte kurser i denne måned.</p>
                <button 
                  onClick={() => setActiveTab("all")} 
                  className="text-brand-primary font-bold text-sm mt-2 hover:underline"
                >
                  Se alle datoer
                </button>
              </div>
            )}
          </div>

          {/* Right Side: Instructor Profile & Contact */}
          <div id="instruktoer" className="lg:col-span-4 space-y-6">
            
            {/* Instructor Card */}
            <div className="bg-white rounded-3xl border border-stone-200 p-6 sm:p-8 shadow-xl relative overflow-hidden text-left">
              <div className="absolute top-0 right-0 h-24 w-24 bg-brand-primary/5 rounded-bl-full -z-10"></div>
              
              <div className="flex items-center gap-4 mb-6">
                <div className="h-16 w-16 rounded-2xl bg-stone-100 border border-stone-200 flex items-center justify-center text-brand-primary shrink-0">
                  <User className="h-8 w-8" />
                </div>
                <div>
                  <h3 className="text-xl font-black text-brand-dark leading-tight">Michael</h3>
                  <p className="text-xs text-stone-500 font-medium">Certificeret Førstehjælpsinstruktør</p>
                </div>
              </div>

              <p className="text-stone-600 text-sm leading-relaxed mb-6">
                Michael står for den færdselsrelaterede førstehjælpsundervisning. Han sikrer, at du kommer igennem alle de lovpligtige emner på en spændende, praktisk og letforståelig måde.
              </p>

              {/* Contact Info List */}
              <div className="space-y-4 border-t border-stone-100 pt-6">
                
                <a 
                  href="tel:61661084" 
                  className="flex items-center gap-3 p-3 rounded-xl hover:bg-stone-50 transition-colors group"
                >
                  <div className="h-9 w-9 rounded-lg bg-stone-50 group-hover:bg-brand-primary/10 text-stone-500 group-hover:text-brand-primary flex items-center justify-center shrink-0 transition-colors">
                    <Phone className="h-4 w-4" />
                  </div>
                  <div>
                    <p className="text-[10px] text-stone-400 font-semibold uppercase tracking-wider">Telefon</p>
                    <p className="text-sm font-bold text-brand-dark group-hover:text-brand-primary transition-colors">61 66 10 84</p>
                  </div>
                </a>

                <a 
                  href="mailto:mb.foerstehjaelp@gmail.com" 
                  className="flex items-center gap-3 p-3 rounded-xl hover:bg-stone-50 transition-colors group"
                >
                  <div className="h-9 w-9 rounded-lg bg-stone-50 group-hover:bg-brand-primary/10 text-stone-500 group-hover:text-brand-primary flex items-center justify-center shrink-0 transition-colors">
                    <Mail className="h-4 w-4" />
                  </div>
                  <div className="min-w-0">
                    <p className="text-[10px] text-stone-400 font-semibold uppercase tracking-wider">E-mail</p>
                    <p className="text-sm font-bold text-brand-dark group-hover:text-brand-primary transition-colors truncate">mb.foerstehjaelp@gmail.com</p>
                  </div>
                </a>

                <a 
                  href="https://maps.google.com/?q=Hasserisgade+5,+9000+Aalborg" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="flex items-center gap-3 p-3 rounded-xl hover:bg-stone-50 transition-colors group"
                >
                  <div className="h-9 w-9 rounded-lg bg-stone-50 group-hover:bg-brand-primary/10 text-stone-500 group-hover:text-brand-primary flex items-center justify-center shrink-0 transition-colors">
                    <MapPin className="h-4 w-4" />
                  </div>
                  <div>
                    <p className="text-[10px] text-stone-400 font-semibold uppercase tracking-wider">Kursussted</p>
                    <p className="text-sm font-bold text-brand-dark group-hover:text-brand-primary transition-colors">Hasserisgade 5, 9000 Aalborg</p>
                  </div>
                </a>

              </div>

              {/* Quick SMS Button */}
              <div className="mt-6 pt-6 border-t border-stone-100">
                <a 
                  href="sms:61661084?body=Hej Michael, jeg vil gerne tilmeldes et førstehjælpskursus."
                  className="block w-full text-center py-3 bg-brand-primary text-white font-bold rounded-xl hover:bg-brand-primary-hover shadow-md transition-all duration-200 text-sm"
                >
                  Send SMS for tilmelding
                </a>
              </div>

            </div>

            {/* Note Box */}
            <div className="bg-amber-50/60 border border-amber-200/60 rounded-2xl p-5 text-left flex gap-3">
              <AlertCircle className="h-5 w-5 text-amber-500 shrink-0 mt-0.5" />
              <div className="text-xs text-amber-800 space-y-1">
                <p className="font-bold">Husk ved tilmelding:</p>
                <p>Oplys dit fulde navn (som det står i dit pas) samt din fødselsdato, når du tilmelder dig hos Michael.</p>
              </div>
            </div>

          </div>

        </div>
      </section>

      {/* Step-by-Step Guide */}
      <section className="py-16 lg:py-24 bg-stone-50 border-y border-stone-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <span className="text-xs font-bold text-brand-primary uppercase tracking-wider bg-brand-primary/10 px-3 py-1.5 rounded-full">
              Processen
            </span>
            <h2 className="text-3xl sm:text-4xl font-black text-brand-dark tracking-tight mt-4">
              Din vej til førstehjælpsbeviset
            </h2>
            <p className="text-stone-600 mt-2 text-base sm:text-lg">
              Det er super nemt at få styr på førstehjælpen. Følg blot disse fire simple trin:
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 relative">
            
            {/* Step 1 */}
            <div className="bg-white p-6 rounded-2xl border border-stone-200/80 shadow-sm relative text-left">
              <div className="absolute -top-5 left-6 h-10 w-10 rounded-xl bg-brand-primary text-white font-black flex items-center justify-center shadow-md">
                1
              </div>
              <h3 className="font-bold text-brand-dark text-lg mt-2 mb-2">Vælg en dato</h3>
              <p className="text-stone-500 text-sm leading-relaxed">
                Kig i vores kursuskalender ovenfor og find en dato, der passer perfekt ind i din kalender.
              </p>
            </div>

            {/* Step 2 */}
            <div className="bg-white p-6 rounded-2xl border border-stone-200/80 shadow-sm relative text-left">
              <div className="absolute -top-5 left-6 h-10 w-10 rounded-xl bg-brand-primary text-white font-black flex items-center justify-center shadow-md">
                2
              </div>
              <h3 className="font-bold text-brand-dark text-lg mt-2 mb-2">Tilmeld dig</h3>
              <p className="text-stone-500 text-sm leading-relaxed">
                Send en SMS til Michael på <span className="font-semibold text-brand-dark">61 66 10 84</span> med dit navn og den ønskede dato.
              </p>
            </div>

            {/* Step 3 */}
            <div className="bg-white p-6 rounded-2xl border border-stone-200/80 shadow-sm relative text-left">
              <div className="absolute -top-5 left-6 h-10 w-10 rounded-xl bg-brand-primary text-white font-black flex items-center justify-center shadow-md">
                3
              </div>
              <h3 className="font-bold text-brand-dark text-lg mt-2 mb-2">Tag kurset</h3>
              <p className="text-stone-500 text-sm leading-relaxed">
                Mød op i Hasserisgade 5 til 8 timers engagerende undervisning. Husk praktisk tøj og godt humør!
              </p>
            </div>

            {/* Step 4 */}
            <div className="bg-white p-6 rounded-2xl border border-stone-200/80 shadow-sm relative text-left">
              <div className="absolute -top-5 left-6 h-10 w-10 rounded-xl bg-brand-primary text-white font-black flex items-center justify-center shadow-md">
                4
              </div>
              <h3 className="font-bold text-brand-dark text-lg mt-2 mb-2">Aflever beviset</h3>
              <p className="text-stone-500 text-sm leading-relaxed">
                Når du har bestået, modtager du et digitalt bevis. Send det til Carina, så hun kan indsende din ansøgning.
              </p>
            </div>

          </div>
        </div>
      </section>

      {/* Call to Action */}
      <section className="py-16 lg:py-20 bg-brand-dark text-white relative overflow-hidden">
        <div className="absolute inset-0 opacity-10 bg-[radial-gradient(#ffffff_1px,transparent_1px)] [background-size:20px_20px]"></div>
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10 text-center space-y-6">
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-black tracking-tight leading-tight text-white">
            Klar til at starte på dit kørekort?
          </h2>
          <p className="text-stone-300 text-base sm:text-lg max-w-2xl mx-auto leading-relaxed">
            Førstehjælp er blot ét af skridtene. Tilmeld dig næste holdstart hos Carinas Køreskole i Gug, og lad os tage rejsen mod dit kørekort sammen under trygge og rolige rammer.
          </p>
          
          <div className="flex flex-wrap justify-center gap-4 pt-4">
            <a 
              href="/kontakt" 
              className="px-8 py-4 bg-brand-primary text-white font-bold rounded-xl hover:bg-brand-primary-hover shadow-lg transition-all duration-200 text-sm"
            >
              Tilmeld dig holdstart nu
            </a>
            <a 
              href="/priser" 
              className="px-8 py-4 bg-white/10 hover:bg-white/20 text-white font-bold rounded-xl transition-all duration-200 text-sm border border-white/10"
            >
              Se priser & beregner
            </a>
          </div>
        </div>
      </section>

    </div>
  );
}
